'use client';
import { Languages } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import type { Locale } from '../../lib/i18n/dictionaries';
import { buttonClassName } from './Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './shadcn/dropdown-menu';

// Language names always render in their own language, independent of the active UI locale.
const LANGS: { value: Locale; name: string }[] = [
  { value: 'en', name: 'English' },
  { value: 'cs', name: 'Čeština' },
  { value: 'sk', name: 'Slovenčina' },
];

/**
 * Accessible single-choice language menu. Radix owns menu focus, keyboard navigation, outside-press and
 * Escape dismissal, focus restoration and collision-aware placement; selection persists via the i18n
 * store.
 *
 * `collapsed` narrows the button to a bare icon (the top bar drops the locale label on small screens).
 * The menu still prefers below and stays end-aligned to that button so it remains reachable on phones.
 *
 * Its box is the shared button scale, taken through `buttonClassName` because a Radix trigger has to stay
 * one plain element: the icon form is the default control step (36px, 44px on a coarse pointer) and the
 * labelled form the same default with its text. It used to state 28px plus an unconditional 44px floor,
 * which made a fine pointer show a 44px language button beside 32px neighbours.
 */
export function LanguageSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const { locale, setLocale, t } = useTranslation();
  const currentName = LANGS.find((language) => language.value === locale)?.name ?? locale.toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${t.common.language}: ${currentName}`}
          title={`${t.common.language}: ${currentName}`}
          className={buttonClassName('outline', collapsed ? 'icon' : 'default', collapsed
            ? 'duration-[var(--motion-fast)] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            : 'duration-[var(--motion-fast)] gap-1.5 px-2.5 text-small text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
        >
          <Languages size={collapsed ? 16 : 18} strokeWidth={1.5} aria-hidden />
          {!collapsed && <span className="language-switcher__name text-eyebrow uppercase">{locale}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent aria-label={t.common.language} align="end" sideOffset={8} className="min-w-36">
        <DropdownMenuRadioGroup value={locale} onValueChange={(value) => setLocale(value as Locale)}>
          {LANGS.map((language) => (
            <DropdownMenuRadioItem
              key={language.value}
              value={language.value}
            >
              {language.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
